import { isAuthFailure } from './authError';

/**
 * The subset of i18next's `t` this module needs. Declared structurally rather than importing
 * `TFunction` so the helper stays trivially callable from a test with a stub.
 */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Turn a thrown API error into the sentence staff should read.
 *
 * 🔴 **Why this is not just `error.message`.** Every load-failure surface in this app used to
 * render the server's raw string — either directly (`error instanceof Error ? error.message`)
 * or interpolated into a `{{error}}` slot. That is fine for a 500 and actively misleading for
 * an authentication failure, because the fail-closed backend answers a credential-less request
 * with `{"error": "No authorization header"}`. Staff therefore saw, verbatim:
 *
 *     Failed to load clients: No authorization header
 *
 * Read literally, that sentence blames the client list. What actually happened is that the
 * session could not be authenticated. The equivalent wrong-noun message on ptc-conversions
 * cost a full triage session against a system that was healthy throughout — the message is
 * the expensive half of this defect, not the missing header.
 *
 * ⚠ **The copy hedges deliberately.** `common.authFailed` says the sign-in *may* have expired
 * rather than that it *did*, because expiry is only one of the five ways
 * {@link import('./authError').AuthFailureReason} can arise, and a message must not assert a
 * cause the code has not established. The key is named `authFailed` and not `sessionExpired`
 * for the same reason: a key name that asserts the wrong cause is how the wrong assumption
 * comes back, via whoever reads the key instead of the string.
 *
 * @param error       whatever react-query surfaced
 * @param t           the caller's `t` from `useTranslation()`
 * @param fallbackKey the surface's own load-failure key. Keys carrying an `{{error}}` slot get
 *                    the underlying message interpolated; keys without one simply ignore it.
 */
export function apiErrorMessage(error: unknown, t: Translate, fallbackKey: string): string {
  // Covers both halves: we could not get a token so nothing was sent, and the server rejected
  // the token we did send. Different facts, same thing for staff to do about it.
  if (isAuthFailure(error)) return t('common.authFailed');

  return t(fallbackKey, { error: rawDetail(error, t) });
}

function rawDetail(error: unknown, t: Translate): string {
  if (error instanceof Error && error.message) return error.message;
  // Non-Error throws reach here (a rejected string, an object from a library). Rendering
  // "[object Object]" into a staff-facing sentence is worse than admitting we don't know.
  return t('common.unknownError');
}
